import React from 'react'
import deepEqual from 'deep-equal'

import { SortableContainer, SortableElement } from '@adalo/react-sortable-hoc'
import { THEMES } from 'constants.js'
import { dataTypes, formatterOptions, numberFormats } from '@adalo/constants'
import { currencies, DEFAULT_CURRENCY } from 'utils/currency'

import { IconButton } from 'components/Shared/Icon'
import { GroupedAccordion } from 'components/Shared/Accordion'
import PrefixSuffix from 'components/Shared/PrefixSuffix'
import ToggleField from 'components/Shared/Forms/ToggleField'

import SimpleTextControl from '../SimpleTextControl'
import MenuControl from '../MenuControl'
import './Table.scss'

const BINDING_OPTIONS = {
  textLength: [
    {
      value: 'singleLine',
      label: 'Single Line',
    },
    {
      value: 'multiLine',
      label: 'Multi Line',
    },
    {
      value: 'noMax',
      label: 'No Max',
    },
  ],
  columnWidth: [
    {
      value: 'standard',
      label: 'Standard',
    },
    {
      value: 'wide',
      label: 'Wide',
    },
  ],
  imageRatio: [
    {
      value: 'landscape',
      label: 'Landscape',
    },
    {
      value: 'portrait',
      label: 'Portrait',
    },
    {
      value: 'square',
      label: 'Square',
    },
    {
      value: 'circle',
      label: 'Circle',
    },
  ],
  locationFormat: [
    {
      value: 'fullAddress',
      label: 'Full Address',
    },
    {
      value: 'addressElements.address1',
      label: 'Street Address',
    },
    {
      value: 'addressElements.name',
      label: 'Name',
    },
    {
      value: 'addressElements.city',
      label: 'City',
    },
    {
      value: 'addressElements.state',
      label: 'State',
    },
    {
      value: 'addressElements.country',
      label: 'Country',
    },
    {
      value: 'addressElements.postalCode',
      label: 'Postal Code',
    },
    {
      value: 'coordinates.latitude',
      label: 'Latitude',
    },
    {
      value: 'coordinates.longitude',
      label: 'Longitude',
    },
  ],
}

const FieldItem = props => {
  const { field, fieldIndex, onChange, onDelete, datasourceCollection } = props

  const fieldsWithAdvancedOptions = new Set([dataTypes.NUMBER])

  const handleDelete = () => {
    onDelete(fieldIndex)
  }

  const updateBindingType = option => {
    if (!field.binding) {
      return null
    }

    const updatedField = {
      ...field,
      binding: {
        ...field.binding,
        format: {
          ...field.binding.format,
          ...option,
        },
      },
    }

    onChange(updatedField, fieldIndex)
  }

  const updateBindingRequiredText = value => {
    const updatedField = {
      ...field,
      binding: {
        ...field.binding,
        format: {
          ...field.binding.format,
          requiredText: value ? 'This field is required' : null,
          required: value,
        },
      },
    }

    onChange(updatedField, fieldIndex)
  }

  const updateBindingFirstColumn = value => {
    const updatedField = {
      ...field,
      binding: {
        ...field.binding,
        format: {
          ...field.binding.format,
          shouldFreezeFirstColumn: value,
        },
      },
    }

    onChange(updatedField, fieldIndex)
  }

  const updateBindingPrefixSuffix = suffix => {
    if (!field.binding) {
      return null
    }

    const { format } = suffix
    delete format.type

    const updatedField = {
      ...field,
      binding: {
        ...field.binding,
        format: {
          ...field.binding.format,
          ...format,
        },
      },
    }

    onChange(updatedField, fieldIndex)
  }

  const updateLabel = currentField => {
    onChange(currentField, fieldIndex)
  }

  const renderAdvanced = () => {
    return (
      <PrefixSuffix
        entity={field.binding}
        onUpdate={updateBindingPrefixSuffix}
      />
    )
  }

  const renderMenu = () => {
    const options = formatterOptions[field.type] || []
    const CURRENCY_TYPES = [
      numberFormats.CURRENCY,
      numberFormats.ABBREVIATED_CURRENCY,
    ]

    const bindingType = field.binding?.format?.type
    const bindingCurrency =
      field.binding?.format?.currency ??
      field.binding?.format?.abbreviatedCurrency
    const bindingTextLength = field.binding?.format?.textLength
    const bindingColumnWidth = field.binding?.format?.columnWidth
    const bindingImageRatio = field.binding?.format?.imageRatio
    const bindingRequired = field.binding?.format?.required
    const bindingRequiredText = field.binding?.format?.requiredText
    const bindingLocationFormat = field.binding?.format?.locationFormat

    switch (field.type) {
      case dataTypes.NUMBER:
        return (
          <>
            <MenuControl
              options={options}
              name="type"
              displayName="Number Format"
              value={bindingType ?? 'Default'}
              onChange={updateBindingType}
              comparator={deepEqual}
              placeholder="Default"
              menuTheme={THEMES.DATA}
            />
            {CURRENCY_TYPES.includes(bindingType) && (
              <MenuControl
                options={currencies}
                name="currency"
                displayName="Currency Type"
                value={bindingCurrency ?? DEFAULT_CURRENCY}
                onChange={updateBindingType}
                comparator={deepEqual}
                placeholder={DEFAULT_CURRENCY}
                menuTheme={THEMES.DATA}
              />
            )}
          </>
        )
      case dataTypes.TEXT:
        return (
          <>
            <MenuControl
              options={BINDING_OPTIONS.textLength}
              name="textLength"
              displayName="Text Length"
              value={bindingTextLength ?? 'Single Line'}
              onChange={updateBindingType}
              comparator={deepEqual}
              placeholder="Single Line"
              menuTheme={THEMES.DATA}
            />
            <MenuControl
              options={BINDING_OPTIONS.columnWidth}
              name="columnWidth"
              displayName="Column Width"
              value={bindingColumnWidth ?? 'Standard'}
              onChange={updateBindingType}
              comparator={deepEqual}
              placeholder="Standard"
              menuTheme={THEMES.DATA}
            />
          </>
        )
      case dataTypes.IMAGE:
        return (
          <>
            <MenuControl
              options={BINDING_OPTIONS.imageRatio}
              name="imageRatio"
              displayName="Image Aspect Ratio"
              value={bindingImageRatio ?? 'Landscape'}
              onChange={updateBindingType}
              comparator={deepEqual}
              placeholder="Landscape"
              menuTheme={THEMES.DATA}
            />
            <MenuControl
              options={BINDING_OPTIONS.columnWidth}
              name="columnWidth"
              displayName="Column Width"
              value={bindingColumnWidth ?? 'Standard'}
              onChange={updateBindingType}
              comparator={deepEqual}
              placeholder="Standard"
              menuTheme={THEMES.DATA}
            />
          </>
        )
      case dataTypes.DATE:
      case dataTypes.DATE_ONLY:
        return (
          <>
            <MenuControl
              options={options}
              name="type"
              displayName="Date Format"
              value={bindingType ?? 'Relative'}
              onChange={updateBindingType}
              comparator={deepEqual}
              placeholder="Relative"
              menuTheme={THEMES.DATA}
            />
          </>
        )
      case dataTypes.BOOLEAN:
        return (
          <ToggleField
            label="Required Error Text"
            value={bindingRequired}
            inputValue={bindingRequired ? bindingRequiredText : 'Not Required'}
            onChange={updateBindingRequiredText}
            boxed
            hideLabelOnDisabled
          />
        )
      case dataTypes.LOCATION:
        return (
          <>
            <MenuControl
              options={BINDING_OPTIONS.locationFormat}
              name="locationFormat"
              displayName="Location Format"
              value={bindingLocationFormat ?? 'Full Address'}
              onChange={updateBindingType}
              comparator={deepEqual}
              placeholder="Full Address"
              menuTheme={THEMES.DATA}
            />
          </>
        )
      default:
        return null
    }
  }

  const renderChildren = () => {
    const bindingFirstColumn = field.binding?.format?.shouldFreezeFirstColumn

    return (
      <div>
        <SimpleTextControl
          displayName="Column Header Text"
          value={field.label}
          name="label"
          onChange={updateLabel}
        />
        {renderMenu()}
        {fieldIndex === 0 && (
          <ToggleField
            label="Freeze First Column"
            value={bindingFirstColumn}
            inputValue={bindingFirstColumn ? 'On' : 'Off'}
            onChange={updateBindingFirstColumn}
            boxed
            hideLabelOnDisabled
          />
        )}
      </div>
    )
  }

  const getCollectionField = () => {
    return datasourceCollection.fields[field.fieldId]
  }

  const renderTitle = () => {
    const collectionField = getCollectionField()

    return (
      <div className="form-inspect-field-title">
        <span>
          {(collectionField && collectionField.name) || '[Deleted Column]'}
        </span>
        <IconButton type="trash-small" onClick={handleDelete} />
      </div>
    )
  }

  if (!field || !datasourceCollection) {
    return null
  }

  const renderAdvancedOptions = fieldsWithAdvancedOptions.has(field.type)
    ? renderAdvanced
    : undefined

  return (
    <div className="form-inspect-field-wrapper">
      <GroupedAccordion
        boxed
        className="form-inspect-field"
        group="form-inspect-field"
        itemId={field.fieldId}
        title={renderTitle()}
        renderChildren={renderChildren}
        renderAdvanced={renderAdvancedOptions}
      />
    </div>
  )
}

const FieldItemsList = props => {
  const {
    items,
    collection,
    datasource,
    onChange,
    onDelete,
    appId,
    object,
    componentId,
  } = props

  if (!datasource) {
    return null
  }

  return (
    <div>
      {items.map((field, i) => (
        <SortableFieldItem
          key={field.fieldId}
          appId={appId}
          object={object}
          componentId={componentId}
          fieldIndex={i}
          index={i}
          field={field}
          datasource={datasource}
          datasourceCollection={collection}
          onChange={onChange}
          onDelete={onDelete}
        />
      ))}
    </div>
  )
}

const SortableFieldItem = SortableElement(FieldItem)

export const SortableFieldItemsList = SortableContainer(FieldItemsList)
